import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Close } from '@videoblocks/react-icons';

import { StockItemFormat } from '../../../../../../common/types/StockItemTypes';
import { FormatButton } from './FormatButton';

interface VideoStockItemDownloaderProps {
  stockItemFormats: StockItemFormat[];
  onSelect?: (
    event: React.MouseEvent<HTMLButtonElement>,
    format: StockItemFormat
  ) => void;
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
}
const VideoStockItemDownloader = ({
  stockItemFormats,
  onSelect,
  onClose,
}: VideoStockItemDownloaderProps) => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [isContainerHovered, setIsContainerHovered] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState<number>(() => {
    // Try to get initial width during mounting
    return containerRef.current?.getBoundingClientRect().width || 500;
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const formatsByResolution = stockItemFormats.reduce(
    (acc, format) => {
      const key = format.resolution;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(format);
      return acc;
    },
    {} as Record<string, StockItemFormat[]>
  );

  const shouldShowSelectedState = !isContainerHovered;

  const getButtonSize = useCallback((containerWidth: number) => {
    if (containerWidth <= 272) {
      return 'small';
    } else if (containerWidth < 350) {
      return 'medium';
    } else {
      return 'large';
    }
  }, []);

  const containerSize = getButtonSize(containerWidth);
  const isContainerSmallSize = containerSize === 'small';
  const isContainerMediumSize = containerSize === 'medium';

  return (
    <div
      ref={containerRef}
      className="download-format-selector view bg-gray-900 bg-opacity-90 rounded-lg h-full w-full flex justify-center items-center"
    >
      <button
        className={twMerge(
          'absolute right-5 top-5 text-white border-0 z-10',
          `${isContainerSmallSize ? 'top-2 right-2' : ''}`
        )}
        onClick={onClose}
        name={'close'}
      >
        <Close className="w-4 h-4" fill="white" />
      </button>
      <div
        className={`flex flex-col w-full relative items-center ${
          isContainerMediumSize ? 'pt-6' : ''
        }`}
      >
        <h2
          className={`text-white text-center mt-0 mb-3 ${
            isContainerMediumSize ? 'text-sm mb-0.5' : 'text-base'
          }`}
        >
          Download
        </h2>
        <div
          className={twMerge(
            `grid grid-cols-2 gap-2 w-full px-2`,
            isContainerSmallSize && 'flex flex-col w-full gap-0.5 px-2 pb-2'
          )}
          onMouseEnter={() => setIsContainerHovered(true)}
          onMouseLeave={() => setIsContainerHovered(false)}
        >
          {Object.values(formatsByResolution).flatMap((formats) =>
            formats.map((format) => {
              const formatId = `${format.formatName}-${format.resolution}`;
              const isSelected =
                selectedFormat?.formatName === format.formatName &&
                selectedFormat?.resolution === format.resolution;
              const isHovered = hoveredId === formatId;
              const isActive =
                (isSelected && shouldShowSelectedState) || isHovered;

              return (
                <FormatButton
                  key={formatId}
                  format={format}
                  formatId={formatId}
                  isActive={isActive}
                  onSelect={(event) => {
                    setSelectedFormat(format);
                    onSelect?.(event, format);
                  }}
                  onHoverStart={() => setHoveredId(formatId)}
                  onHoverEnd={() => setHoveredId(null)}
                  size={containerSize}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoStockItemDownloader;
